import { ApiError } from '../errors';
import { notifySessionEnded, sessionToken } from './session';

/** Where the local service listens. The desktop shell talks to it over loopback only. */
export const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:8420/api/v1';

/** The origin the service answers from (`http://127.0.0.1:8420`): what a path the service returns, such as a logo, is relative to. */
const API_ORIGIN = new URL(API_URL).origin;

/** A path the service handed out (`/api/v1/platform/logo?...`) as an address a picture can be loaded from; anything else is left alone. */
export function absoluteUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return path.startsWith('/') ? `${API_ORIGIN}${path}` : path;
}

interface RequestOptions {
  body?: unknown;
  /** A file upload (multipart): the browser sets the content type and its boundary. */
  form?: FormData;
  /** False for calls made before anyone is signed in. A 401 there is an answer ("wrong password"), not the end of a session. */
  authenticated?: boolean;
  /** A file comes back (a PDF to save), not JSON. A failure still comes back as JSON and is turned into an `ApiError` as usual. */
  as?: 'blob';
}

interface ErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, string[]>;
  requestId?: string;
}

/**
 * One call to the backend: JSON in and out, the session secret as a Bearer header, and every failure turned into an `ApiError` with
 * the server's own code, message and per-field details. A service that does not answer becomes `network_error`, which is what shows
 * the "service unavailable" screen. A 401 on a call made with a session means it has ended, and the app is told.
 */
export async function request<T>(method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, { body, form, authenticated = true, as }: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: as === 'blob' ? 'application/pdf, application/json' : 'application/json' };
  if (body !== undefined && !form) headers['Content-Type'] = 'application/json';
  const token = authenticated ? sessionToken.get() : null;
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { method, headers, body: form ?? (body === undefined ? undefined : JSON.stringify(body)) });
  } catch {
    throw new ApiError(0, 'network_error', 'The application service is not responding.');
  }

  if (response.status === 204) return undefined as T;
  if (as === 'blob' && response.ok) return (await response.blob()) as T;
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    /* an empty or non-JSON body: handled below */
  }

  if (!response.ok) {
    const failure = (payload ?? {}) as ErrorBody;
    if (response.status === 401 && token) notifySessionEnded();
    throw new ApiError(response.status, failure.code ?? 'server_error', failure.message ?? 'Something went wrong on the service.', {
      requestId: failure.requestId ?? response.headers.get('X-Request-Id') ?? undefined,
      details: failure.details && Object.keys(failure.details).length ? failure.details : undefined,
    });
  }
  return payload as T;
}
